import React from 'react';
import { Button, Tooltip } from 'antd';
import { CheckOutlined, DeleteOutlined, EditOutlined, GatewayOutlined, UndoOutlined } from '@ant-design/icons';
import { MIN_VERTICES } from '../constants';
import { DrawMode } from '../types';

interface Props {
    /**
     * 距地图左边缘的距离 (px)。列表收起时展开手柄贴在地图左缘,
     * 工具栏要让开这段宽度, 否则两个浮层叠在一起。
     */
    offsetLeft?: number;
    drawMode: DrawMode;
    /** 是否有选中 AOI。 */
    hasSelection: boolean;
    /** 是否有刚画完、还没保存的草稿面 (草稿同样可以改形状 / 丢弃)。 */
    hasDraft: boolean;
    /** 绘制中已点的顶点数 (0 时没什么可撤销, < 3 时完不成)。 */
    drawVertexCount: number;
    /** 有 UPSERT 权限才显示 (由父级包 HasResource, 这里不重复判)。 */
    onStartDraw: () => void;
    onToggleEditShape: () => void;
    onDeleteSelected: () => void;
    /** 绘制中: 撤销刚点的顶点。 */
    onUndoVertex: () => void;
    /** 绘制中: 用已点顶点完成绘制 (闭合成面)。 */
    onFinishDraw: () => void;
}

/** 工具项统一样式: 图标 + 文字左对齐, 高度和字号收紧, 避免大块遮挡地图。 */
const ITEM_STYLE: React.CSSProperties = {
    height: 32,
    paddingLeft: 10,
    paddingRight: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    fontSize: 13,
};

/**
 * 地图左上角工具栏: 绘制 AOI / 编辑形状 / 删除面。
 *
 * <p>带文字标签 (纯图标不好认), 但用紧凑高度 + 纯文本按钮压住体积;
 * 禁用态由 Tooltip 说明原因, 不让用户点了没反应还不知道为什么。
 */
const MapToolbar: React.FC<Props> = ({
    offsetLeft = 12,
    drawMode,
    hasSelection,
    hasDraft,
    drawVertexCount,
    onStartDraw,
    onToggleEditShape,
    onDeleteSelected,
    onUndoVertex,
    onFinishDraw,
}) => {
    const drawing = drawMode === 'draw';
    const editing = drawMode === 'edit';
    // 草稿(还没保存)和已存在的 AOI 一样可以改形状 / 丢弃; 绘制进行中先闭合再谈改形状
    const shapeTarget = !drawing && (hasSelection || hasDraft);

    return (
        <div
            className="absolute flex flex-col rounded-lg bg-white p-1 shadow-md"
            style={{ top: 12, left: offsetLeft, zIndex: 1200, minWidth: 116 }}
        >
            <Tooltip title="单击逐点描点, 点「完成绘制」收尾" placement="right">
                <Button
                    block
                    type={drawing ? 'primary' : 'text'}
                    icon={<GatewayOutlined />}
                    style={ITEM_STYLE}
                    onClick={onStartDraw}
                >
                    {drawing ? '退出绘制' : '绘制 AOI'}
                </Button>
            </Tooltip>
            {drawing && (
                <>
                    <div style={{ height: 1, background: '#f0f0f0', margin: '2px 6px' }} />
                    <div style={{ padding: '2px 10px', fontSize: 12, color: '#8c96a5' }}>
                        已点 {drawVertexCount} 个顶点
                    </div>
                    <Tooltip title={drawVertexCount > 0 ? '点错了撤回刚才那个顶点' : '还没点过顶点'} placement="right">
                        <Button
                            block
                            type="text"
                            icon={<UndoOutlined />}
                            style={ITEM_STYLE}
                            disabled={drawVertexCount === 0}
                            onClick={onUndoVertex}
                        >
                            撤销上一点
                        </Button>
                    </Tooltip>
                    <Tooltip
                        title={
                            drawVertexCount >= MIN_VERTICES
                                ? '用已点的顶点闭合成面, 进入属性填写'
                                : `至少 ${MIN_VERTICES} 个顶点才能完成`
                        }
                        placement="right"
                    >
                        <Button
                            block
                            type="text"
                            icon={<CheckOutlined />}
                            style={ITEM_STYLE}
                            disabled={drawVertexCount < MIN_VERTICES}
                            onClick={onFinishDraw}
                        >
                            完成绘制
                        </Button>
                    </Tooltip>
                </>
            )}
            <div style={{ height: 1, background: '#f0f0f0', margin: '2px 6px' }} />
            <Tooltip
                title={
                    // eslint-disable-next-line no-nested-ternary
                    drawing
                        ? '先完成当前绘制, 再调整顶点'
                        : shapeTarget
                            ? '拖顶点改形状, 拖边中点加点, 右键顶点删除'
                            : '请先选中一个 AOI, 或画一个新面'
                }
                placement="right"
            >
                <Button
                    block
                    type={editing ? 'primary' : 'text'}
                    icon={<EditOutlined />}
                    style={ITEM_STYLE}
                    disabled={!editing && !shapeTarget}
                    onClick={onToggleEditShape}
                >
                    {editing ? '完成编辑' : '编辑形状'}
                </Button>
            </Tooltip>
            <Tooltip
                title={
                    // eslint-disable-next-line no-nested-ternary
                    hasDraft
                        ? '丢弃刚画的面 (还没保存, 不会动线上数据)'
                        : hasSelection
                            ? '删除选中的 AOI (需二次确认)'
                            : '请先选中一个 AOI, 或画一个新面'
                }
                placement="right"
            >
                <Button
                    block
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    style={ITEM_STYLE}
                    disabled={!hasDraft && !hasSelection}
                    onClick={onDeleteSelected}
                >
                    {hasDraft ? '丢弃草稿' : '删除面'}
                </Button>
            </Tooltip>
        </div>
    );
};

export default MapToolbar;
